/**
 * Lightweight in-memory buffer for per-call RPC samples.
 *
 * Replaces the removed metrics-buffer/beacon path: instead of POSTing raw
 * per-call batches to a server route (Prometheus/Grafana on the old k8s
 * runtime), it collects samples client-side and, on each flush window, forwards
 * a single aggregated `rpc_usage` wide event per group through the logger.
 */
import { isE2EObservabilityDisabled } from '@/lib/observability/test-mode'

import type { RpcCallSample } from './aggregate'
import { emitRpcUsageEvent, RPC_USAGE_FLUSH_WINDOW_SECONDS } from './emit'

const FLUSH_INTERVAL_MS = RPC_USAGE_FLUSH_WINDOW_SECONDS * 1000
/** Force a flush before the window elapses if the buffer grows large. */
const MAX_BUFFER_SIZE = 500

class RpcUsageBuffer {
  private samples: RpcCallSample[] = []
  private windowStartMs = 0
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private initialized = false

  private init(): void {
    if (this.initialized || typeof window === 'undefined') return
    this.initialized = true

    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS)

    // Flush pending samples when the tab is backgrounded or unloaded so a
    // window's worth of usage isn't lost on navigation.
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush()
    })
    window.addEventListener('pagehide', () => this.flush())
  }

  record(sample: RpcCallSample): void {
    if (isE2EObservabilityDisabled() || typeof window === 'undefined') return

    if (!this.initialized) this.init()

    // Stamp the window start on the first sample so an early flush reports the
    // span actually covered, not the configured interval.
    if (this.samples.length === 0) this.windowStartMs = performance.now()

    this.samples.push(sample)
    if (this.samples.length >= MAX_BUFFER_SIZE) this.flush()
  }

  private flush(): void {
    if (this.samples.length === 0) return
    const samples = this.samples
    const windowSeconds = (performance.now() - this.windowStartMs) / 1000
    this.samples = []
    emitRpcUsageEvent(samples, windowSeconds)
  }
}

const rpcUsageBuffer = new RpcUsageBuffer()

export function recordRpcCallSample(sample: RpcCallSample): void {
  rpcUsageBuffer.record(sample)
}
